import React, { useMemo, useContext, useEffect } from 'react';
import { Text } from '@prisjakt/ui-translate/src/translate-func';
import { AppContext } from '@prisjakt/ui-app-context';
import * as S from './styles';
import { TableHeader, Column } from './table-header';
import {
  Product,
  ProductCollectionSortDirection,
  ProductCollectionPropertyColumn,
} from '../../../../../types';
import { getSortKeyForProperty } from '../../../utils/properties';
import { getPropertyColumnName } from '../../../utils/property-columns';
import { DEFAULT_PROPERTY_COLUMN_COUNT } from '../constants';
import HighlightedSearchResult from '../highlighted-search-result';
import { ContainerAdsTable } from './ads-container';
import { ProductRow } from './product-row';
import { ProductComparisonProps } from '../types';
import { useNewColumnIndication } from './new-column-indication';

type DefaultColumn = {
  translation: Text<'category'>;
  sortKey?: string;
  colSpan?: number;
};

const defaultColumns: DefaultColumn[] = [
  {
    translation: 'product-list.name-column-heading',
    sortKey: 'name',
  },
  { translation: 'product-list.price-column-heading', sortKey: 'price' },
  {
    translation: 'product-list.stock-column-heading',
    sortKey: 'property.in_stock',
  },
  {
    translation: 'product-list.rating-column-heading',
    sortKey: 'user_reviews',
  },
];

type Props = {
  products: Product[];
  currentSort: string;
  sortDirection: ProductCollectionSortDirection;
  propertyColumns: ProductCollectionPropertyColumn[];
} & ProductComparisonProps;

export const TableLayout = ({
  products,
  currentSort,
  sortDirection,
  propertyColumns,
  ...productComparisonProps
}: Props) => {
  const { t } = useContext(AppContext);

  const columns = useMemo<Column[]>(() => {
    return [
      ...defaultColumns.map(column => ({
        ...column,
        name: t('category', column.translation) as string,
      })),
      ...propertyColumns.map(column => ({
        name: getPropertyColumnName(column, t),
        sortKey: getSortKeyForProperty(column.id),
      })),
    ];
  }, [propertyColumns, t]);

  const {
    isIndicatingNewColumn,
    onFinishedIndicatingNewColumn,
    setColumnCount,
    scrollableRef,
  } = useNewColumnIndication(columns.length);

  useEffect(() => {
    setColumnCount(columns.length);
  }, [setColumnCount, columns.length]);

  return (
    <S.AdPositioningContext>
      <S.HorizontalScrollPane ref={scrollableRef}>
        <S.Table
          scrollable={propertyColumns.length > DEFAULT_PROPERTY_COLUMN_COUNT}
          cellSpacing="0"
          data-test="ProductLayoutTable"
        >
          <TableHeader
            currentSort={currentSort}
            sortDirection={sortDirection}
            columns={columns}
            isIndicatingNewColumn={isIndicatingNewColumn}
            onFinishedIndicatingNewColumn={onFinishedIndicatingNewColumn}
          />
          <tbody>
            <ContainerAdsTable numOfColumns={4 + propertyColumns.length}>
              {products.map(product => {
                return (
                  <ProductRow
                    key={product.id}
                    product={product}
                    propertyColumns={propertyColumns}
                    isIndicatingNewColumn={isIndicatingNewColumn}
                    highlightedName={
                      product.highlight ? (
                        <HighlightedSearchResult>
                          {product.highlight}
                        </HighlightedSearchResult>
                      ) : (
                        undefined
                      )
                    }
                    {...productComparisonProps}
                  />
                );
              })}
            </ContainerAdsTable>
          </tbody>
        </S.Table>
      </S.HorizontalScrollPane>
    </S.AdPositioningContext>
  );
};
